/**
 * Shared auth POST helper (plan §2.2.2 · AD-10).
 *
 * Every `/auth/*` POST goes through here so three rules hold in one place
 * rather than once per caller. The session `GET` does not — it needs its own
 * 401-vs-other-non-OK handling, and shares only the body-reading rule via
 * `read-optional-json.ts`. Neither does `logout`, deliberately: it sends no
 * `Accept`/`Content-Type` and tolerates a non-OK response, so routing it here
 * would change the wire and start throwing where it currently shrugs.
 *
 *  1. The URL and the `credentials` mode come from `authEndpoint` — never
 *     rebuilt, so same-origin vs cross-origin stays a single decision.
 *  2. Success is decided from `res.ok` FIRST and the body is read with
 *     `res.text()`. `res.json()` throws on a body-less 204, which is exactly
 *     what `authenticate/verify` returns on success; a verified ceremony must
 *     never fail on body shape.
 *  3. Failure is a typed error carrying the HTTP status and the `ApiResponse`
 *     envelope's `error.code`, so callers classify on data rather than by
 *     sniffing a message string.
 *
 * `postAuthJson` returns `unknown` deliberately. A generic `<T>` would be a
 * cast wearing a type parameter; callers narrow explicitly at the call site.
 */
import type { AppConfig } from '@/config';

import { authEndpoint } from './auth-context';
import { readOptionalJson } from './read-optional-json';

/** A response was received and it was not OK. `code` is null when the envelope carries none. */
export class AuthFetchError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;

  constructor(status: number, code: string | null, body: unknown) {
    /* No 'fetch' or 'Network' substring: a revived message-sniff classifier
       must not treat a server rejection as a transport failure. */
    super(`Auth request rejected with status ${String(status)}`);
    this.name = 'AuthFetchError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** `fetch` itself rejected — no response, no status. The original error is the `cause`. */
export class AuthNetworkError extends Error {
  constructor(cause: unknown) {
    super('Auth request could not reach the server.', { cause });
    this.name = 'AuthNetworkError';
  }
}

/**
 * Let a throwable that is not a transport outcome escape to the host.
 *
 * Both credential surfaces fold everything unrecognised into one uniform
 * message — correct for server states, which security.md §3 requires to be
 * indistinguishable. But the same arm catches throwables that are not server
 * states at all: a TypeError from a refactor, a bad `config` shape. Rendering
 * those as "Sign-in failed. Try again." offers a remedy — retype and retry —
 * that can never clear a programming error, and with no client telemetry they
 * would otherwise leave no trace anywhere. The user still sees the
 * byte-identical copy; only the diagnostic escapes, asynchronously, so the
 * form's own state settles first.
 *
 * It lives beside `AuthFetchError`/`AuthNetworkError` because the question it
 * answers — "is this a transport outcome?" — is exactly what those two classes
 * define. `isExpected` extends the allow-list for a caller with its own
 * non-transport expected failures; the passkey ceremony has several (a
 * cancelled prompt, an unsupported browser, a tagged ceremony stage), and the
 * password form has none.
 */
export function reportUnexpected(err: unknown, isExpected?: (err: unknown) => boolean): void {
  if (err instanceof AuthFetchError || err instanceof AuthNetworkError) return;
  if (isExpected?.(err) === true) return;
  escapeToHost(err);
}

/**
 * Report only the CLOSED set of JavaScript programming errors.
 *
 * The inverse of `reportUnexpected`, and the distinction is the whole point.
 * `reportUnexpected` works by excluding known-expected failures, which is
 * sound where that set is closed — the password form's is exactly
 * `AuthFetchError | AuthNetworkError`. It is NOT sound for the passkey
 * ceremony: `@simplewebauthn/browser` wraps platform failures in a
 * `WebAuthnError` carrying any DOMException name (`NotAllowedError`,
 * `InvalidStateError`, `AbortError`, `SecurityError`, …) and the platform can
 * raise bare DOMExceptions besides, so an exclusion list that misses one turns
 * an ordinary ceremony failure into an uncaught host error.
 *
 * Inverting fixes that. `TypeError`, `ReferenceError` and `SyntaxError` are
 * never legitimate outcomes of a ceremony — transport failures are already
 * wrapped as `AuthNetworkError` before they reach a caller — so each one is a
 * defect in our own code (a bad `config` shape, a malformed options object
 * reaching the WebAuthn call, a refactor slip). Reporting exactly those keeps
 * the diagnostic escape hatch without ever misclassifying a domain error.
 */
export function reportProgrammingError(err: unknown): void {
  if (err instanceof TypeError || err instanceof ReferenceError || err instanceof SyntaxError) {
    escapeToHost(err);
  }
}

/**
 * Rethrow asynchronously, so the caller's own state settles first and the user
 * still sees the uniform copy while the diagnostic reaches the host.
 */
function escapeToHost(err: unknown): void {
  queueMicrotask(() => {
    throw err;
  });
}

export async function postAuthJson(
  config: AppConfig,
  path: string,
  body?: unknown,
): Promise<unknown> {
  const endpoint = authEndpoint(config, path);
  /* A body-less POST sends no Content-Type: an empty body has no media type to
     declare, and announcing JSON without one invites a 415. */
  const headers: Record<string, string> =
    body === undefined
      ? { Accept: 'application/json' }
      : { Accept: 'application/json', 'Content-Type': 'application/json' };
  /* `method` and `headers` follow the spread, so an endpoint that ever grows
     either field cannot silently take over the verb or drop `Accept`. */
  const init: RequestInit = {
    ...endpoint.request,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    method: 'POST',
    headers,
  };

  const res = await send(endpoint.url, init);
  const parsed = await readOptionalJson(res);
  if (res.ok) return parsed;
  throw new AuthFetchError(res.status, extractCode(parsed), parsed);
}

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    throw new AuthNetworkError(cause);
  }
}

function envelopeError(body: unknown): unknown {
  if (typeof body !== 'object' || body === null || !('error' in body)) return undefined;
  return body.error;
}

function extractCode(body: unknown): string | null {
  const error = envelopeError(body);
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  return typeof error.code === 'string' ? error.code : null;
}
